import React, { useState, useEffect } from 'react';
import { useAuth } from '../../USER/Auth/AuthContext';
import { requestPayment, markPaymentAsPaid, confirmPayment, getServicePayment } from '../services/deliveryServices';
import { getPaymentMethods } from '../../../services/roleViewsServices';
import Swal from 'sweetalert2';
import { QRCodeCanvas } from 'qrcode.react';
import { PAYMENT_STATUSES, PAYMENT_METHODS, SERVICE_STATUSES } from '../../../constants';
import { Banknote, CheckCircle2, XCircle, QrCode, Loader2 } from 'lucide-react';
import { BrySkeleton } from '../../UI/BryUI';

const paymentIcons = {
  [PAYMENT_STATUSES.PENDING]: Banknote,
  [PAYMENT_STATUSES.PAID]: CheckCircle2,
  [PAYMENT_STATUSES.CONFIRMED]: CheckCircle2,
  [PAYMENT_STATUSES.CANCELLED]: XCircle,
  [PAYMENT_STATUSES.DISPUTED]: XCircle,
};

const paymentColors = {
  [PAYMENT_STATUSES.PENDING]: { bg: 'bg-[#E6F8F9]', text: 'text-[#088D91]', icon: 'var(--bry-accent)', darkBg: 'bg-gray-800', darkText: 'text-[var(--bry-accent)]' },
  [PAYMENT_STATUSES.PAID]: { bg: 'bg-[#E8F5E9]', text: 'text-[#2E7D32]', icon: '#4CAF50', darkBg: 'bg-green-900/30', darkText: 'text-green-400' },
  [PAYMENT_STATUSES.CONFIRMED]: { bg: 'bg-[#E8F5E9]', text: 'text-[#1B5E20]', icon: '#4CAF50', darkBg: 'bg-green-900/30', darkText: 'text-green-400' },
  [PAYMENT_STATUSES.CANCELLED]: { bg: 'bg-[#F4F8FB]', text: 'text-[#333333]', icon: '#333333', darkBg: 'bg-gray-800', darkText: 'text-gray-300' },
  [PAYMENT_STATUSES.DISPUTED]: { bg: 'bg-[#F4F8FB]', text: 'text-[#333333]', icon: '#333333', darkBg: 'bg-gray-800', darkText: 'text-gray-300' },
};

const methodLabels = {
  [PAYMENT_METHODS.PAGO_MOVIL]: 'Pago Móvil',
  [PAYMENT_METHODS.BINANCE]: 'Binance P2P',
  [PAYMENT_METHODS.EFECTIVO]: 'Efectivo',
  divisa: 'Divisa',
  zelle: 'Zelle',
};

function PaymentPanel({ service, onRefresh }) {
  const { currentUser, isDriver, isPassenger } = useAuth();
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [driverMethods, setDriverMethods] = useState([]);
  const [selectedDriverMethod, setSelectedDriverMethod] = useState(null);

  // Form states
  const [amount, setAmount] = useState(service?.price || '');
  const [currency, setCurrency] = useState('VES');
  const [paymentMethod, setPaymentMethod] = useState(PAYMENT_METHODS.PAGO_MOVIL);
  const [reference, setReference] = useState('');

  const fetchPayment = async () => {
    if (!service?.service_id) return;
    setFetching(true);
    try {
      const data = await getServicePayment(service.service_id);
      setPayment(data && data.id ? data : null);
    } catch (err) {
      setPayment(null);
    }
    setFetching(false);
  };

  useEffect(() => {
    fetchPayment();
    if (service?.driver_id && isPassenger) {
      getPaymentMethods(service.driver_id).then(methods => {
        setDriverMethods(methods || []);
        if (methods && methods.length > 0) {
          setSelectedDriverMethod(methods.find(m => m.is_default) || methods[0]);
          setPaymentMethod(methods.find(m => m.is_default)?.type || methods[0].type);
        }
      }).catch(console.error);
    }
  }, [service?.service_id, service?.driver_id, isPassenger]);

  const handleRequestPayment = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      Swal.fire('Error', 'Ingresa un monto válido', 'warning');
      return;
    }
    setLoading(true);
    try {
      const result = await requestPayment(service.service_id, parseFloat(amount), currency, paymentMethod);
      if (result.success) {
        setShowForm(false);
        await fetchPayment();
        if (onRefresh) onRefresh();
      } else {
        Swal.fire('Error', result.error || 'No se pudo solicitar el pago', 'error');
      }
    } catch (err) {
      Swal.fire('Error', err.message, 'error');
    }
    setLoading(false);
  };

  const handleMarkAsPaid = async () => {
    if (!reference.trim()) {
      Swal.fire('Error', 'Ingresa la referencia del pago', 'warning');
      return;
    }
    setLoading(true);
    try {
      const result = await markPaymentAsPaid(payment.id, reference.trim(), paymentMethod);
      if (result.success) {
        setShowForm(false);
        setReference('');
        await fetchPayment();
        if (onRefresh) onRefresh();
      } else {
        Swal.fire('Error', result.error || 'No se pudo marcar como pagado', 'error');
      }
    } catch (err) {
      Swal.fire('Error', err.message, 'error');
    }
    setLoading(false);
  };

  const handleConfirmPayment = async () => {
    setLoading(true);
    try {
      const result = await confirmPayment(payment.id);
      if (result.success) {
        await fetchPayment();
        if (onRefresh) onRefresh();
      } else {
        Swal.fire('Error', result.error || 'No se pudo confirmar', 'error');
      }
    } catch (err) {
      Swal.fire('Error', err.message, 'error');
    }
    setLoading(false);
  };

  const panelBg = isDriver ? 'bg-gray-900 border-t border-gray-800' : 'bg-gray-50 border-t border-gray-200';

  if (fetching) {
    return (
      <div className={`px-4 py-3 ${panelBg}`}>
        <div className={`rounded-xl p-4 shadow-sm ${isDriver ? 'bg-gray-800' : 'bg-white'} border ${isDriver ? 'border-gray-700' : 'border-gray-100'}`}>
          <div className="flex items-center gap-2 mb-4">
            <BrySkeleton type="custom" width="20px" height="20px" className="rounded-full" />
            <BrySkeleton type="title" width="40%" className="!mb-0" />
          </div>
          <div className="space-y-3 mb-4">
            <div className="flex justify-between items-center">
              <BrySkeleton type="text" width="20%" />
              <BrySkeleton type="text" width="30%" />
            </div>
            <div className="flex justify-between items-center">
              <BrySkeleton type="text" width="20%" />
              <BrySkeleton type="text" width="40%" />
            </div>
          </div>
          {isDriver && <BrySkeleton type="custom" width="100%" height="44px" className="rounded-full mt-4" />}
        </div>
      </div>
    );
  }

  const statusColors = payment ? paymentColors[payment.status] || paymentColors[PAYMENT_STATUSES.PENDING] : paymentColors[PAYMENT_STATUSES.PENDING];
  
  const IconComponent = payment ? paymentIcons[payment.status] || Banknote : Banknote;

  // Driver UI classes
  const cardBg = isDriver ? statusColors.darkBg : statusColors.bg;
  const textColor = isDriver ? statusColors.darkText : statusColors.text;
  const primaryBtn = isDriver ? 'bg-[var(--bry-accent)] hover:bg-[#0c959a] text-white' : 'bg-[var(--bry-accent)] hover:bg-[#0c959a] text-white';
  const successBtn = 'bg-green-600 hover:bg-green-700 text-white';
  const outlineBtn = isDriver ? 'border border-[var(--bry-accent)] text-[var(--bry-accent)] hover:bg-[var(--bry-accent)]/10' : `border border-[var(--bry-accent)] text-[var(--bry-accent)] hover:bg-gray-100`;

  return (
    <div className={`px-4 py-3 ${panelBg}`}>
      {payment ? (
        <div className={`rounded-xl p-4 shadow-sm ${cardBg}`}>
          <div className="flex items-center gap-2 mb-3">
            <IconComponent size={20} color={isDriver ? (payment.status === PAYMENT_STATUSES.PENDING ? 'var(--bry-accent)' : '#4ade80') : statusColors.icon} />
            <span className={`font-bold text-sm ${textColor}`}>
              {payment.status === PAYMENT_STATUSES.PENDING && 'Pago solicitado'}
              {payment.status === PAYMENT_STATUSES.PAID && 'Pasajero marcó como pagado'}
              {payment.status === PAYMENT_STATUSES.CONFIRMED && 'Pago confirmado'}
              {payment.status === PAYMENT_STATUSES.CANCELLED && 'Pago cancelado'}
            </span>
          </div>

          <div className={`text-sm space-y-1.5 ${isDriver ? 'text-gray-300' : textColor}`}>
            <div className="flex justify-between items-center">
              <span>Monto:</span>
              <span className="font-bold text-base">{payment.amount} {payment.currency}</span>
            </div>
            <div className="flex justify-between items-center">
              <span>Método:</span>
              <span>{methodLabels[payment.payment_method] || payment.payment_method}</span>
            </div>
            {payment.reference && (
              <div className="flex justify-between items-center">
                <span>Referencia:</span>
                <span className="font-bold">{payment.reference}</span>
              </div>
            )}
          </div>

          {isDriver && payment.status === PAYMENT_STATUSES.PAID && (
            <button
              className={`w-full mt-4 py-2.5 rounded-full font-bold transition-colors flex items-center justify-center ${successBtn} disabled:opacity-50`}
              onClick={handleConfirmPayment}
              disabled={loading}
            >
              {loading ? <Loader2 className="animate-spin" size={18} /> : 'Confirmar Pago Recibido'}
            </button>
          )}

          {isDriver && payment.status === PAYMENT_STATUSES.CONFIRMED && (
            <div className="mt-4 text-center p-4 rounded-xl bg-gray-800 shadow-sm border border-green-500/30">
              <h6 className="font-bold text-green-400 mb-2 flex items-center justify-center gap-2">
                <QrCode size={18} /> Código de Evaluación
              </h6>
              <p className="text-xs text-gray-400 mb-3">Pide al pasajero que escanee este código para evaluar el servicio.</p>
              <div className="flex justify-center bg-white p-3 rounded-lg mx-auto w-fit">
                <QRCodeCanvas 
                  value={`${window.location.origin}/evaluaciones?serviceId=${service.service_id}`} 
                  size={140} 
                  level={"H"}
                  includeMargin={true}
                />
              </div>
            </div>
          )}

          {isPassenger && payment.status === PAYMENT_STATUSES.PENDING && !showForm && (
            <button
              className={`w-full mt-4 py-2.5 rounded-full font-bold transition-colors flex items-center justify-center gap-2 ${primaryBtn} disabled:opacity-50`}
              onClick={() => { setShowForm(true); setReference(''); }}
              disabled={loading}
            >
              <CheckCircle2 size={18} />
              Marcar como Pagado
            </button>
          )}
        </div>
      ) : isDriver && service?.status === SERVICE_STATUSES.COMPLETED && (
        <>
          {!showForm ? (
            <button
              className={`w-full py-2.5 rounded-full font-bold flex items-center justify-center gap-2 transition-colors ${outlineBtn}`}
              onClick={() => setShowForm(true)}
            >
              <Banknote size={18} />
              Solicitar Pago
            </button>
          ) : (
            <div className="bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-700">
              <p className="font-bold mb-3 text-sm text-gray-200">Solicitar Pago</p>
              
              <div className="mb-3">
                <input
                  type="number"
                  step="0.01"
                  placeholder="Monto"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 text-white rounded-full text-sm focus:outline-none focus:border-[var(--bry-accent)] focus:ring-1 focus:ring-[var(--bry-accent)]"
                />
              </div>
              
              <div className="flex gap-2 mb-4">
                <select 
                  value={currency} 
                  onChange={(e) => setCurrency(e.target.value)}
                  className="flex-1 px-4 py-2 bg-gray-900 border border-gray-700 text-white rounded-full text-sm appearance-none focus:outline-none focus:border-[var(--bry-accent)]"
                >
                  <option value="VES">VES (Bs)</option>
                  <option value="USD">USD ($)</option>
                </select>
                <select 
                  value={paymentMethod} 
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="flex-[2] px-4 py-2 bg-gray-900 border border-gray-700 text-white rounded-full text-sm appearance-none focus:outline-none focus:border-[var(--bry-accent)]"
                >
                  <option value={PAYMENT_METHODS.PAGO_MOVIL}>Pago Móvil</option>
                  <option value={PAYMENT_METHODS.BINANCE}>Binance P2P</option>
                  <option value={PAYMENT_METHODS.EFECTIVO}>Efectivo</option>
                  <option value="divisa">Divisa</option>
                  <option value="zelle">Zelle</option>
                </select>
              </div>
              
              <div className="flex gap-3">
                <button
                  className="flex-1 py-2 rounded-full text-sm font-medium text-gray-300 bg-gray-700 hover:bg-gray-600 transition-colors"
                  onClick={() => setShowForm(false)}
                >
                  Cancelar
                </button>
                <button
                  className={`flex-1 py-2 rounded-full text-sm font-bold flex justify-center items-center ${primaryBtn} disabled:opacity-50`}
                  onClick={handleRequestPayment}
                  disabled={loading}
                >
                  {loading ? <Loader2 className="animate-spin" size={18} /> : 'Solicitar'}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {isPassenger && payment?.status === PAYMENT_STATUSES.PENDING && showForm && (
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 mt-3">
          <p className="font-bold mb-3 text-sm text-gray-800">Confirmar Pago</p>
          
          {driverMethods.length > 0 ? (
            <div className="mb-4">
              <p className="text-gray-500 mb-2 text-xs font-medium">Selecciona dónde transferiste:</p>
              <select 
                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 text-gray-800 rounded-full text-sm focus:outline-none focus:border-[var(--bry-accent)] mb-3 appearance-none"
                value={selectedDriverMethod?.id || ''}
                onChange={(e) => {
                  const m = driverMethods.find(x => x.id === e.target.value);
                  if (m) {
                    setSelectedDriverMethod(m);
                    setPaymentMethod(m.type);
                  }
                }}
              >
                {driverMethods.map(m => (
                  <option key={m.id} value={m.id}>{m.label} ({methodLabels[m.type] || m.type})</option>
                ))}
              </select>

              {/* Mostrar detalles del método de pago */}
              {selectedDriverMethod && selectedDriverMethod.details && (
                <div className="p-3 rounded-xl bg-gray-50 text-xs border border-gray-100 space-y-1.5 text-gray-600">
                  {selectedDriverMethod.type === PAYMENT_METHODS.PAGO_MOVIL && (
                    <>
                      <div className="flex justify-between"><span>Banco:</span> <span className="font-bold text-gray-800">{selectedDriverMethod.details.bank}</span></div>
                      <div className="flex justify-between"><span>Teléfono:</span> <span className="font-bold text-gray-800">{selectedDriverMethod.details.phone}</span></div>
                      <div className="flex justify-between"><span>Cédula/RIF:</span> <span className="font-bold text-gray-800">{selectedDriverMethod.details.document}</span></div>
                    </>
                  )}
                  {selectedDriverMethod.type === PAYMENT_METHODS.TRANSFERENCIA && (
                    <>
                      <div className="flex justify-between"><span>Banco:</span> <span className="font-bold text-gray-800">{selectedDriverMethod.details.bank}</span></div>
                      <div className="flex justify-between"><span>Cuenta:</span> <span className="font-bold text-gray-800">{selectedDriverMethod.details.account}</span></div>
                      <div className="flex justify-between"><span>Titular:</span> <span className="font-bold text-gray-800">{selectedDriverMethod.details.holder}</span></div>
                    </>
                  )}
                  {(selectedDriverMethod.type !== PAYMENT_METHODS.PAGO_MOVIL && selectedDriverMethod.type !== PAYMENT_METHODS.TRANSFERENCIA) && (
                    <div className="text-gray-400 italic">No hay detalles adicionales.</div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <select 
              value={paymentMethod} 
              onChange={(e) => setPaymentMethod(e.target.value)} 
              className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 text-gray-800 rounded-full text-sm focus:outline-none focus:border-[var(--bry-accent)] mb-3 appearance-none"
            >
              <option value={PAYMENT_METHODS.PAGO_MOVIL}>Pago Móvil</option>
              <option value={PAYMENT_METHODS.BINANCE}>Binance P2P</option>
              <option value={PAYMENT_METHODS.EFECTIVO}>Efectivo</option>
              <option value="divisa">Divisa</option>
              <option value="zelle">Zelle</option>
            </select>
          )}

          <div className="mb-4">
            <input
              type="text"
              placeholder="Número de referencia"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 text-gray-800 rounded-full text-sm focus:outline-none focus:border-[var(--bry-accent)]"
            />
          </div>
          
          <div className="flex gap-3">
            <button
              className="flex-1 py-2.5 rounded-full text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
              onClick={() => setShowForm(false)}
            >
              Cancelar
            </button>
            <button
              className={`flex-[2] py-2.5 rounded-full text-sm font-bold flex justify-center items-center gap-2 ${successBtn} disabled:opacity-50`}
              onClick={handleMarkAsPaid}
              disabled={loading}
            >
              {loading ? <Loader2 className="animate-spin" size={16} /> : 'Confirmar Pago'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default PaymentPanel;
